import { zodResolver } from '@hookform/resolvers/zod';
import omit from 'lodash/omit';
import { JSX, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TextareaHighlight } from '@/components/ui/textarea-highlight';
import {
  FileMimeTypes,
  FilePicker,
  ItemStatus,
  S3FileUploader,
} from '@/modules/common';

import { maxImagePreviewFileSize } from '../constants';
import { StylesUploadUrlData } from '../types';
import { StyleFormSchema } from '../validations';

type StyleFormValues = z.infer<typeof StyleFormSchema>;

interface StyleFormProps {
  editMode?: boolean;
  uploadUrlData?: StylesUploadUrlData | null;
  defaultValues?: StyleFormValues;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: StyleFormValues) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
}

const defaultFormValues = {
  name: '',
  positivePrompts: '',
  negativePrompts: '',
  gptPromptTemplate: '{user_prompt}, {security_prompt} ',
  userPromptTemplate: '{user_prompt} ',
  publicUrl: '',
  loraEnabled: false,
  loraModelTriggerWord: '',
  updatedAt: undefined,
  loraModelStrength: undefined,
};

export function StyleForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
  uploadUrlData,
  refetchUploadCredentials,
}: StyleFormProps) {
  const form = useForm<StyleFormValues>({
    resolver: zodResolver(StyleFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async () => {
    const data = form.getValues();

    await onSubmit(
      omit(
        {
          ...data,
          loraModelStrength: data.loraEnabled
            ? data.loraModelStrength
            : undefined,
        },
        ['loraEnabled']
      )
    );
  };

  const revalidateFormInEmptyFile = (fileKey: string) => {
    if (editMode) {
      form.reset({
        ...defaultValues,
        [fileKey]: '',
      });
    }
  };

  const filesConfig = useMemo(() => {
    const previewFileConfig = {
      key: 'imagePublicUrl',
      uploadUrl: uploadUrlData?.styleImageUploadUrl,
      fileUrl: defaultValues?.imagePublicUrl,
    };

    if (defaultValues?.loraEnabled) {
      return [
        previewFileConfig,
        {
          key: 'loraModelFilePublicUrl',
          uploadUrl: uploadUrlData?.loraModelFileUploadUrl,
          fileUrl: defaultValues?.imagePublicUrl,
          fileName: 'Lora Model File',
        },
      ];
    } else {
      return [previewFileConfig];
    }
  }, [defaultValues, uploadUrlData]);

  return (
    <Form {...form}>
      <S3FileUploader
        config={filesConfig}
        onComplete={handleFormSubmit}
        onError={revalidateFormInEmptyFile}
        refetchCredentials={refetchUploadCredentials}
      >
        {({
          retryUpload,
          filesState,
          isUploading,
          onFileChange,
          uploadFiles,
        }) => (
          <form onSubmit={form.handleSubmit(uploadFiles)}>
            {children({ isLoading: isUploading })}

            <div className="flex flex-col gap-10 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Style name</FormLabel>
                      <FormControl>
                        <Input placeholder="Add style name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="updatedAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last modified</FormLabel>
                      <FormControl>
                        <Input
                          disabled
                          placeholder="YYYY-MM-DD"
                          required={false}
                          readOnly
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ItemStatus
                  name="status"
                  isDisabled={!editMode}
                  control={form.control}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <div className="col-span-2">
                  <FilePicker
                    control={form.control}
                    label="Style preview"
                    name="imagePublicUrl"
                    accept={[
                      FileMimeTypes.Jpeg,
                      FileMimeTypes.Png,
                      FileMimeTypes.Jpg,
                    ]}
                    onRetry={retryUpload}
                    withFilePreview
                    isUploading={isUploading}
                    file={filesState.imagePublicUrl?.file}
                    error={filesState.imagePublicUrl?.error}
                    onFileChange={onFileChange}
                    placeholder="Add style preview picture"
                    tips={
                      <p className="text-sm text-zinc-500">
                        The file must be in
                        <span className="text-zinc-100">
                          &nbsp;.png, .jpeg, .jpg&nbsp;
                        </span>
                        formats and maximum size 10 MB.
                      </p>
                    }
                    maxFileSize={maxImagePreviewFileSize}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="positivePrompts"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Positive prompt</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe a positive prompt"
                          maxLength={1000}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="negativePrompts"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Negative prompt</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe a negative prompt"
                          maxLength={1000}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                <h4 className="col-span-1 lg:col-span-2 xl:col-span-2 text-2xl font-semibold">
                  AI prompt templates
                </h4>

                <FormField
                  control={form.control}
                  name="gptPromptTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GPT Prompt Template</FormLabel>
                      <FormControl>
                        <TextareaHighlight
                          placeholder="{security_prompt}, {user_prompt}"
                          maxLength={5000}
                          searchWords={['{security_prompt}', '{user_prompt}']}
                          placeholderClassName="text-secondary bg-secondary-foreground"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="userPromptTemplate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>User Prompt Template</FormLabel>
                      <FormControl>
                        <TextareaHighlight
                          placeholder="{user_prompt}"
                          maxLength={5000}
                          searchWords={['{user_prompt}']}
                          placeholderClassName="text-secondary bg-secondary-foreground"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        )}
      </S3FileUploader>
    </Form>
  );
}
